/**
 * EngineAuditPanel — Engine Audit panel for the Analysis stage
 *
 * Phase 4.4 — redesign-p4-analatt-44b
 * Exposes engine audit trail/provenance data from existing backend endpoints
 * Wraps the existing @/components/EngineAuditPanel for tab integration
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Download, RefreshCw, AlertCircle, CheckCircle2, HelpCircle, BarChart3, Shield, FileCheck } from 'lucide-react';
import { isMoneyKey, isScoreKey, OUTPUT_KEY_PRIORITY } from '@/lib/engineOutputKeys';
import { FinancialValue } from '@/components/shared/FinancialValue';
import { MetricCard } from '@/components/shared/MetricCard';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { DataTable, Column } from '@/components/ui/DataTable';
import { Button } from '@/components/ui/Button';
import { api } from '@/utils/api';
import { BRAND_NAME } from '@/config/brand';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface EngineProvenance {
  method_name: string;
  version: string;
}

interface EngineAuditEntry {
  engine_id: string;
  output_value: Record<string, unknown> | null;
  method: EngineProvenance;
  computed_at: string | null;
  freshness_status: 'FRESH' | 'STALE';
  error_state: string | null;
  support_status: 'CONTESTED' | 'SUPPORTED_CONDITIONAL' | 'SUPPORTED';
}

interface EngineAuditData {
  report_id: string;
  engines: EngineAuditEntry[];
  missing: string[];
  generated_at: string;
}

interface AuditChainEntry {
  timestamp: string;
  event_type: string;
  engine_id?: string;
  hash: string;
  previous_hash: string;
  signature?: string;
}

interface AuditIntegrityData {
  report_id: string;
  chain_valid: boolean;
  entries: AuditChainEntry[];
  verification_timestamp: string;
}

// ---------------------------------------------------------------------------
// Output Value Renderer
// ---------------------------------------------------------------------------

export { isMoneyKey, isScoreKey };

export function OutputValueRenderer({ value }: { value: Record<string, unknown> | null }) {
  if (!value) {
    return (
      <span className="font-mono text-sm text-[var(--color-text-ghost)]" style={{ fontFamily: 'DM Mono, ui-monospace' }}>
        —
      </span>
    );
  }

  // Try to find a meaningful value to display
  const keyPriority = OUTPUT_KEY_PRIORITY;

  function renderValue(key: string, v: unknown) {
    if (typeof v === 'number') {
      if (isMoneyKey(key)) {
        return <FinancialValue value={v} />;
      }
      if (isScoreKey(key)) {
        // Render scores as plain numbers (backend may emit 0–30 sub-scores
        // or 0–1 proportions). Never guess a denominator.
        return (
          <span className="font-mono text-sm text-[var(--color-text-secondary)]" style={{ fontFamily: 'DM Mono, ui-monospace' }}>
            {v}
          </span>
        );
      }
      return (
        <span className="font-mono text-sm text-[var(--color-text-secondary)]">
          {v}
        </span>
      );
    }
    if (typeof v === 'string') {
      return (
        <span className="font-mono text-sm text-[var(--color-text-secondary)]">
          {v}
        </span>
      );
    }
    return null;
  }

  for (const key of keyPriority) {
    if (value[key] !== undefined && value[key] !== null) {
      const rendered = renderValue(key, value[key]);
      if (rendered) return rendered;
    }
  }

  // Classified fallback: only scan remaining keys when they are explicitly
  // typed as money or score. Unclassified keys fall through to "N fields"
  // so backend whitelist keys (impact_period_days, coinsurance_factor, …)
  // never render as bare unlabelled numbers.
  const remainingKeys = Object.keys(value).filter(k => !keyPriority.includes(k));
  for (const key of remainingKeys) {
    if (value[key] !== undefined && value[key] !== null) {
      if (isMoneyKey(key) || isScoreKey(key)) {
        const rendered = renderValue(key, value[key]);
        if (rendered) return rendered;
      }
    }
  }

  const keys = Object.keys(value);
  return (
    <span className="font-mono text-xs text-[var(--color-text-muted)]" style={{ fontFamily: 'DM Mono, ui-monospace' }}>
      {keys.length} fields
    </span>
  );
}

// ---------------------------------------------------------------------------
// Provenance Renderer
// ---------------------------------------------------------------------------

function ProvenanceRenderer({ method }: { method: EngineProvenance }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="font-mono text-xs text-[var(--color-text-secondary)]" style={{ fontFamily: 'DM Mono, ui-monospace' }}>
        {method.method_name}
      </span>
      <span className="font-mono text-xs text-[var(--color-text-muted)]" style={{ fontFamily: 'DM Mono, ui-monospace' }}>
        v{method.version}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

interface EngineAuditPanelProps {
  reportId: string;
}

export function EngineAuditPanel({ reportId }: EngineAuditPanelProps) {
  const [data, setData] = useState<EngineAuditData | null>(null);
  const [integrityData, setIntegrityData] = useState<AuditIntegrityData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showIntegrity, setShowIntegrity] = useState(false);

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.get<EngineAuditData>(
        `/api/v1/attorney-tools/engine-audit/${reportId}`,
        { showErrorToast: false }
      );
      if (!result || typeof result !== 'object' || Array.isArray(result)) {
        throw new Error('Invalid response from engine audit endpoint');
      }
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  const fetchIntegrity = useCallback(async () => {
    try {
      const result = await api.get<AuditIntegrityData>(
        `/api/v1/audit-chain/${reportId}/verify`,
        { showErrorToast: false }
      );
      setIntegrityData(result);
    } catch (err) {
      console.error('Integrity check failed:', err);
    }
  }, [reportId]);

  useEffect(() => {
    fetchAudit();
  }, [fetchAudit]);

  useEffect(() => {
    if (showIntegrity) {
      fetchIntegrity();
    }
  }, [showIntegrity, fetchIntegrity]);

  const handleExport = useCallback(() => {
    if (!data) return;

    const exportData = {
      ...data,
      exported_from: BRAND_NAME,
      export_timestamp: new Date().toISOString(),
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `engine-audit-${reportId}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [data, reportId]);

  // Table columns definition
  const columns: Column<EngineAuditEntry>[] = [
    {
      key: 'engine_id',
      label: 'Engine',
      sortable: true,
      width: '180px',
      render: (value: string) => (
        <span
          className="font-mono font-medium text-[var(--color-text-primary)]"
          style={{ fontFamily: 'DM Mono, ui-monospace' }}
        >
          {value}
        </span>
      ),
    },
    {
      key: 'output_value',
      label: 'Output',
      sortable: false,
      width: '200px',
      render: (value: Record<string, unknown> | null) => <OutputValueRenderer value={value} />,
    },
    {
      key: 'method',
      label: 'Provenance',
      sortable: false,
      width: '160px',
      render: (value: EngineProvenance) => <ProvenanceRenderer method={value} />,
    },
    {
      key: 'freshness_status',
      label: 'Freshness',
      sortable: true,
      width: '120px',
      render: (value: 'FRESH' | 'STALE') => (
        <StatusBadge
          text={value}
          variant={value === 'FRESH' ? 'done' : 'attention'}
          size="sm"
        />
      ),
    },
    {
      key: 'support_status',
      label: 'Support',
      sortable: true,
      width: '140px',
      render: (value: 'CONTESTED' | 'SUPPORTED_CONDITIONAL' | 'SUPPORTED') => {
        const variantMap: Record<string, Parameters<typeof StatusBadge>[0]['variant']> = {
          CONTESTED: 'attention',
          SUPPORTED_CONDITIONAL: 'neutral',
          SUPPORTED: 'done',
        };
        return (
          <StatusBadge
            text={value === 'SUPPORTED_CONDITIONAL' ? 'CONDITIONAL' : value}
            variant={variantMap[value] || 'neutral'}
            size="sm"
          />
        );
      },
    },
    {
      key: 'error_state',
      label: 'Status',
      sortable: false,
      width: '120px',
      render: (value: string | null, row: EngineAuditEntry) => {
        if (value) {
          return <StatusBadge text="Error" variant="attention" size="sm" />;
        }
        if (row.freshness_status === 'STALE') {
          return <StatusBadge text="Re-run" variant="warning" size="sm" />;
        }
        return <StatusBadge text="OK" variant="done" size="sm" />;
      },
    },
  ];

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-[var(--color-brand)]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl bg-[rgba(255,102,0,0.08)] p-4">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-[var(--color-brand)]" />
          <p className="text-sm text-[var(--color-text-primary)]">{error}</p>
        </div>
        <button
          onClick={fetchAudit}
          className="mt-3 rounded-md bg-[var(--color-brand)] px-4 py-2 text-sm font-medium text-[var(--color-on-brand)] hover:bg-[var(--color-brand-hover)]"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!data || data.engines.length === 0) {
    return (
      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-6">
        <div className="py-12 text-center">
          <HelpCircle className="mx-auto h-8 w-8 text-[var(--color-text-ghost)]" />
          <p className="mt-3 text-sm text-[var(--color-text-muted)]">No engine outputs found for this case.</p>
        </div>
      </div>
    );
  }

  const freshCount = data.engines.filter((e) => e.freshness_status === 'FRESH').length;
  const contestedCount = data.engines.filter((e) => e.support_status === 'CONTESTED').length;
  const errorCount = data.engines.filter((e) => e.error_state).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-[var(--color-needs-human-tint)] p-2">
            <BarChart3 className="h-5 w-5 text-[var(--color-brand)]" />
          </div>
          <div>
            <h2
              className="text-lg font-semibold text-[var(--color-text-primary)]"
              style={{ fontFamily: 'DM Serif Display, Georgia, serif' }}
            >
              Engine Audit
            </h2>
            <p className="text-xs text-[var(--color-text-muted)]">
              {data.engines.length} engines • Generated {new Date(data.generated_at).toLocaleString()}
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExport}
          className="gap-2 border-[var(--color-brand)] text-[var(--color-brand)] hover:bg-[var(--color-brand)] hover:text-[var(--color-on-brand)]"
        >
          <Download className="h-4 w-4" />
          Export
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <MetricCard
          label="Total Engines"
          value={data.engines.length}
        />
        <MetricCard
          label="Fresh"
          value={freshCount}
          badge={{ text: 'Current', variant: 'done' }}
        />
        <MetricCard
          label="Contested"
          value={contestedCount}
          badge={contestedCount > 0 ? { text: 'Review', variant: 'attention' } : undefined}
          accent={contestedCount > 0}
        />
        <MetricCard
          label="Errors"
          value={errorCount}
          badge={errorCount > 0 ? { text: 'Fix', variant: 'blocked' } : { text: 'None', variant: 'done' }}
        />
      </div>

      {/* Missing engines notice */}
      {data.missing.length > 0 && (
        <div className="rounded-lg bg-[rgba(255,102,0,0.04)] p-3">
          <p className="text-xs text-[var(--color-text-muted)]">
            <span className="font-medium text-[var(--color-brand)]">{data.missing.length}</span> engines not yet run:{''}
            <span className="ml-1 font-mono text-[var(--color-text-dim)]">{data.missing.join(', ')}</span>
          </p>
        </div>
      )}

      {/* Data Table */}
      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
        <DataTable
          columns={columns}
          data={data.engines}
          emptyMessage="No engine data available"
          className="border-0"
        />
      </div>

      {/* Audit Integrity Section */}
      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
        <button
          onClick={() => setShowIntegrity(!showIntegrity)}
          className="flex w-full items-center justify-between p-4 hover:bg-[var(--color-bg-hover)] transition-colors"
        >
          <div className="flex items-center gap-3">
            <Shield className="h-4 w-4 text-[var(--color-brand)]" />
            <span className="text-sm font-medium text-[var(--color-text-secondary)]">
              Audit Chain Integrity
            </span>
            <span className="text-xs text-[var(--color-text-muted)]">— Tamper-evident verification</span>
          </div>
          <span className="text-xs text-[var(--color-brand)]">{showIntegrity ? 'Hide' : 'Show'}</span>
        </button>

        {showIntegrity && (
          <div className="border-t border-[var(--color-border-sub)] px-5 pb-5">
            {integrityData ? (
              <div className="space-y-3 pt-4">
                <div className="flex items-center gap-2">
                  <StatusBadge
                    text={integrityData.chain_valid ? 'VERIFIED' : 'INVALID'}
                    variant={integrityData.chain_valid ? 'done' : 'attention'}
                  />
                  <span className="text-xs text-[var(--color-text-muted)]">
                    Verified at {new Date(integrityData.verification_timestamp).toLocaleString()}
                  </span>
                </div>

                <div className="space-y-2">
                  {integrityData.entries.slice(0, 5).map((entry, idx) => (
                    <div key={idx} className="rounded-md bg-[var(--color-bg-elevated)] p-2 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="text-[var(--color-text-secondary)]">
                          {entry.event_type}
                          {entry.engine_id && ` • ${entry.engine_id}`}
                        </span>
                        <span className="text-[var(--color-text-muted)]">
                          {new Date(entry.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="mt-1 font-mono text-[10px] text-[var(--color-text-dim)]">
                        Hash: {entry.hash.slice(0, 16)}…
                      </div>
                    </div>
                  ))}
                  {integrityData.entries.length > 5 && (
                    <p className="text-center text-xs text-[var(--color-text-muted)]">
                      +{integrityData.entries.length - 5} more entries
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-5 w-5 animate-spin text-[var(--color-brand)] mr-2" />
                <span className="text-sm text-[var(--color-text-muted)]">Verifying integrity…</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default EngineAuditPanel;
